import { motion } from "framer-motion";
import { Heart, Clock, Headphones, Bell } from "lucide-react";

const modules = [
  {
    icon: Heart,
    title: "Afirmaciones Diarias",
    desc: "Feed personalizado por mood: calma, gratitud, enfoque o descanso. Desliza y encuentra tu mantra.",
    color: "from-rose-500/10 to-transparent",
  },
  {
    icon: Clock,
    title: "Temporizador de Rituales",
    desc: "Micro-rituales de 3 a 15 minutos. Configura tu duracion ideal y sumérgete.",
    color: "from-amber-500/10 to-transparent",
  },
  {
    icon: Headphones,
    title: "Audio Ambiental",
    desc: "Paisajes sonoros curados para cada aroma. Lluvia suave, bosque, oceano o silencio guiado.",
    color: "from-sky-500/10 to-transparent",
  },
  {
    icon: Bell,
    title: "Recordatorios Suaves",
    desc: "Notificaciones calidas y no intrusivas que te invitan a tu momento de bienestar diario.",
    color: "from-emerald-500/10 to-transparent",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function WellnessSection() {
  return (
    <section
      id="bienestar"
      data-testid="wellness-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="text-center mb-16 md:mb-20"
        >
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-4">
            Modulo de Bienestar
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-4xl md:text-5xl font-normal tracking-tight leading-tight mb-6 text-white"
            data-testid="wellness-heading"
          >
            Tu companera de
            <br />
            <span className="italic text-[#D4AF37]">bienestar diario</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-base md:text-lg text-white/50 leading-relaxed max-w-2xl mx-auto"
          >
            Inspirado en la experiencia de Inside, el modulo de bienestar de
            Serena es 100% gratuito y esta disenado para convertir el uso de tu
            vela en un habito de calma.
          </motion.p>
        </motion.div>

        {/* Wellness image */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="mb-16 md:mb-20"
        >
          <div className="relative max-w-4xl mx-auto overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1603669435608-eb647988e585?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx3b21hbiUyMG1lZGl0YXRpbmclMjB3ZWxsbmVzcyUyMGRhcmslMjBhZXN0aGV0aWMlMjBsdXh1cnl8ZW58MHx8fHwxNzcyMDc5OTk2fDA&ixlib=rb-4.1.0&q=85"
              alt="Momento de bienestar con Serena"
              className="w-full aspect-[21/9] object-cover"
              data-testid="wellness-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/30" />
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="font-serif text-2xl md:text-4xl italic text-white/90 text-center px-8">
                "Respira. Estas exactamente donde necesitas estar."
              </p>
            </div>
          </div>
        </motion.div>

        {/* Module cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {modules.map((m, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ y: -5, transition: { duration: 0.3 } }}
              className="relative bg-[#0F0F0F] border border-[#27272a] p-8 group hover:border-[#D4AF37]/30 transition-colors duration-500"
              data-testid={`wellness-card-${i}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-b ${m.color} opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
              <div className="relative z-10">
                <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/20 mb-6">
                  <m.icon size={18} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-3 uppercase tracking-wide">
                  {m.title}
                </h3>
                <p className="text-xs text-white/40 leading-relaxed">
                  {m.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Free badge */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mt-12 text-center"
        >
          <span
            className="inline-block border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-widest px-6 py-2"
            data-testid="wellness-free-badge"
          >
            100% gratuito por tiempo limitado
          </span>
        </motion.div>
      </div>
    </section>
  );
}
